/**
 * Delivery Rules Tab - Manage delivery rules for analysis results to CRM
 *
 * Uses existing routing components and hooks from features/routing
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERY_RULE_PRIORITY_MAX } from '@aica/core';
import {
  Plus,
  AlertCircle,
  Save,
  Trash2,
  GripVertical,
  ToggleLeft,
  ToggleRight,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/modals';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
} from '@/components/ui/sheet';
import {
  useRoutingRules,
  useCreateRoutingRule,
  useUpdateRoutingRule,
  useDeleteRoutingRule,
  useToggleRoutingRule,
} from '@/features/routing/hooks/useRoutingRules';
import {
  useCrmFields,
  transformFieldsForQueryBuilder,
} from '@/features/routing/hooks/useCrmFields';
import { RoutingRuleBuilder, CrmFieldsCard } from '@/features/routing/components';
import {
  getRoutingFields,
  mergeCustomCrmFields,
  enrichFieldsWithDepartments,
} from '@/features/routing/constants/routing-fields';
import { useDepartments } from '@/features/routing/hooks';
import type {
  DeliveryRule,
  CreateDeliveryRuleRequest,
  CrmEntityType,
  CrmFieldsEntityResponse,
} from '@/lib/api/types';

interface DeliveryRulesTabProps {
  projectId: string;
}

export function DeliveryRulesTab({ projectId }: DeliveryRulesTabProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRule, setEditingRule] = useState<DeliveryRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<DeliveryRule | null>(null);

  // Fetch delivery rules for this project
  const { data: rules, isLoading, error } = useRoutingRules({ projectId });

  // Mutations
  const createMutation = useCreateRoutingRule();
  const updateMutation = useUpdateRoutingRule();
  const deleteMutation = useDeleteRoutingRule();
  const toggleMutation = useToggleRoutingRule();

  const handleCreate = () => {
    setEditingRule(null);
    setIsEditing(true);
  };

  const handleEdit = (rule: DeliveryRule) => {
    setEditingRule(rule);
    setIsEditing(true);
  };

  const handleSave = async (data: CreateDeliveryRuleRequest) => {
    if (editingRule) {
      await updateMutation.mutateAsync({ id: editingRule.id, data });
    } else {
      await createMutation.mutateAsync({ ...data, projectId });
    }
    setIsEditing(false);
    setEditingRule(null);
  };

  const handleDelete = (id: string) => {
    const rule = rules?.data?.find((r: DeliveryRule) => r.id === id) ?? null;
    setRuleToDelete(rule);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;
    try {
      await deleteMutation.mutateAsync(ruleToDelete.id);
    } catch {
      // useDeleteRoutingRule's onError already surfaces the failure toast
    } finally {
      setRuleToDelete(null);
    }
  };

  const handleToggle = async (id: string) => {
    await toggleMutation.mutateAsync(id);
  };

  if (isLoading) {
    return <TableSkeleton rows={5} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <AlertCircle className="h-8 w-8 text-danger" />
        <p className="text-fg-muted">
          {t('common:common.errorLoading', 'Failed to load data')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium text-fg mb-2">
            {t('deliveryRules.title', 'Delivery Rules')}
          </h2>
          <p className="text-sm text-fg-muted">
            {t(
              'deliveryRules.description',
              'Configure how analysis results are delivered to your CRM fields.'
            )}
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          {t('deliveryRules.addRule', 'Add Rule')}
        </Button>
      </div>

      {/* CRM Fields Status */}
      <CrmFieldsCard compact />

      {/* Rules List */}
      {!rules?.data || rules.data.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-fg-muted mb-4">
            {t('deliveryRules.emptyState', 'No delivery rules configured yet.')}
          </p>
          <Button variant="outline" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t('deliveryRules.createFirst', 'Create your first rule')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.data.map((rule: DeliveryRule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
              isDeleting={deleteMutation.isPending}
              isToggling={toggleMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Tips */}
      <div className="rounded bg-info-dim border border-border p-4 text-sm text-info-fg">
        <p className="font-medium mb-1">{t('deliveryRules.tipsTitle', 'Tips')}</p>
        <ul className="list-disc list-inside text-info-fg space-y-1">
          <li>
            {t(
              'deliveryRules.tip1',
              'Rules with a lower priority number are evaluated first'
            )}
          </li>
          <li>
            {t(
              'deliveryRules.tip2',
              'Use conditions to control when results are delivered'
            )}
          </li>
          <li>
            {t('deliveryRules.tip3', 'Map analysis fields to your CRM custom fields')}
          </li>
        </ul>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={ruleToDelete !== null}
        onClose={() => setRuleToDelete(null)}
        onConfirm={() => void confirmDelete()}
        title={t('deliveryRules.deleteTitle', 'Delete Delivery Rule?')}
        message={t(
          'deliveryRules.confirmDelete',
          'Are you sure you want to delete this rule?'
        )}
        variant="danger"
      />

      {/* Edit Sheet */}
      <Sheet open={isEditing} onOpenChange={(open) => !open && setIsEditing(false)}>
        <SheetContent className="w-full sm:max-w-2xl bg-bg-elevated overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingRule
                ? t('deliveryRules.editRule', 'Edit Delivery Rule')
                : t('deliveryRules.createRule', 'Create Delivery Rule')}
            </SheetTitle>
          </SheetHeader>
          <SheetBody>
            <DeliveryRuleForm
              rule={editingRule}
              projectId={projectId}
              onSave={handleSave}
              onCancel={() => setIsEditing(false)}
              isSaving={createMutation.isPending || updateMutation.isPending}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

interface RuleCardProps {
  rule: DeliveryRule;
  onEdit: (rule: DeliveryRule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  isDeleting: boolean;
  isToggling: boolean;
}

function RuleCard({
  rule,
  onEdit,
  onDelete,
  onToggle,
  isDeleting,
  isToggling,
}: RuleCardProps) {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <GripVertical className="h-5 w-5 text-fg-muted flex-shrink-0 mt-1 cursor-grab" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-medium text-fg truncate">{rule.name}</h3>
            <Badge variant={rule.isEnabled ? 'success' : 'default'}>
              {rule.isEnabled
                ? t('deliveryRules.active', 'Active')
                : t('deliveryRules.disabled', 'Disabled')}
            </Badge>
            <Badge variant="info" className="text-xs">
              {t('deliveryRules.priorityValue', 'Priority: {{value}}', {
                value: rule.priority,
              })}
            </Badge>
          </div>

          {rule.description && (
            <p className="text-sm text-fg-muted mb-2">{rule.description}</p>
          )}

          <div className="flex items-center gap-4 text-xs text-fg-muted">
            <span>
              {t('deliveryRules.target', 'Target: {{value}}', {
                value: rule.targetEntityType,
              })}
            </span>
            <span>
              {t('deliveryRules.fields', 'Fields: {{count}}', {
                count: rule.fieldsToUpdate?.length || 0,
              })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-md"
            onClick={() => onToggle(rule.id)}
            disabled={isToggling}
          >
            {rule.isEnabled ? (
              <ToggleRight className="h-4 w-4 text-success" />
            ) : (
              <ToggleLeft className="h-4 w-4 text-fg-muted" />
            )}
          </Button>
          <Button variant="ghost" size="icon-md" onClick={() => onEdit(rule)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-md"
            onClick={() => onDelete(rule.id)}
            disabled={isDeleting}
            className="text-danger hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface DeliveryRuleFormProps {
  rule: DeliveryRule | null;
  projectId: string;
  onSave: (data: CreateDeliveryRuleRequest) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

function DeliveryRuleForm({
  rule,
  onSave,
  onCancel,
  isSaving,
  projectId,
}: DeliveryRuleFormProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [targetEntityType, setTargetEntityType] = useState<
    'deal' | 'lead' | 'contact' | 'company'
  >(rule?.targetEntityType || 'deal');
  const [priority, setPriority] = useState<number | ''>(rule?.priority ?? '');
  const [conditions, setConditions] = useState<Record<string, unknown>>(
    rule?.conditions || {}
  );

  // Fetch CRM fields for the selected entity type
  const { data: crmFieldsResponse } = useCrmFields({
    entityType: targetEntityType as CrmEntityType,
    enabled: true,
  });

  // Fetch departments for dropdown
  const { data: departments } = useDepartments();

  // Merge base routing fields with dynamic CRM fields
  const mergedFields = useMemo(() => {
    let fields = getRoutingFields(t);
    const responseData = crmFieldsResponse?.data;

    if (responseData) {
      // When entityType is provided, response is CrmFieldsEntityResponse
      if (
        'fields' in responseData &&
        'entityType' in responseData &&
        Array.isArray(responseData.fields)
      ) {
        const entityResponse = responseData as CrmFieldsEntityResponse;
        const transformedFields = transformFieldsForQueryBuilder(entityResponse);
        fields = mergeCustomCrmFields(transformedFields, getRoutingFields(t), t);
      }
    }

    // Enrich department field with dropdown values
    return enrichFieldsWithDepartments(fields, departments);
  }, [crmFieldsResponse, departments, t]);

  const handleConditionsChange = (jsonLogic: Record<string, unknown> | null) => {
    setConditions(jsonLogic || {});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      projectId,
      name,
      description,
      targetEntityType: targetEntityType as 'deal' | 'lead' | 'contact' | 'company',
      priority: priority === '' ? undefined : priority,
      conditions,
      fieldsToUpdate: rule?.fieldsToUpdate || [],
      isEnabled: rule?.isEnabled ?? true,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-fg mb-2">
            {t('deliveryRules.nameLabel', 'Rule Name')} *
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder={t('deliveryRules.namePlaceholder', 'e.g., High Quality Deals')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-2">
            {t('deliveryRules.descriptionLabel', 'Description')}
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minRows={2}
            placeholder={t(
              'deliveryRules.descriptionPlaceholder',
              'Optional description...'
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-fg mb-2">
              {t('deliveryRules.targetEntityLabel', 'Target Entity')}
            </label>
            <Select
              value={targetEntityType}
              onValueChange={(val) =>
                setTargetEntityType(val as 'deal' | 'lead' | 'contact' | 'company')
              }
              options={[
                { value: 'deal', label: t('deliveryRules.entityDeal', 'Deal') },
                { value: 'lead', label: t('deliveryRules.entityLead', 'Lead') },
                { value: 'contact', label: t('deliveryRules.entityContact', 'Contact') },
                { value: 'company', label: t('deliveryRules.entityCompany', 'Company') },
              ]}
              size="sm"
            />
          </div>

          <div>
            <label
              htmlFor="delivery-rule-priority"
              className="block text-sm font-medium text-fg mb-2"
            >
              {t('deliveryRules.priorityLabel', 'Priority')}
            </label>
            <Input
              id="delivery-rule-priority"
              type="number"
              min={1}
              max={DELIVERY_RULE_PRIORITY_MAX}
              required={rule !== null}
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder={
                rule
                  ? undefined
                  : t('deliveryRules.priorityPlaceholder', 'After the last rule')
              }
            />
          </div>
        </div>
      </div>

      {/* Conditions */}
      <div>
        <label className="block text-sm font-medium text-fg mb-2">
          {t('deliveryRules.conditionsLabel', 'Conditions')}
        </label>
        <p className="text-xs text-fg-muted mb-3">
          {t('deliveryRules.conditionsHint', 'Define when this rule should be applied')}
        </p>
        <div className="border border-border rounded-lg p-4 bg-bg-surface">
          <RoutingRuleBuilder
            initialValue={conditions || undefined}
            onChange={handleConditionsChange}
            showPreview
            compact
            fields={mergedFields}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
        <Button
          type="submit"
          isLoading={isSaving}
          disabled={isSaving || !name.trim()}
          size="sm"
        >
          {!isSaving && <Save className="h-4 w-4 mr-2" />}
          {t('common:common.saveChanges', 'Save Changes')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t('common:common.cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  );
}
